var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DialogContent, Box, Typography, DialogFooter, useToast, Alert, Dialog, Loader, Button, } from '@pocketlaw/tetris';
import { useCreateDraft } from 'app/domains/templates';
import { FormDialog, CancelButton, SubmitButton } from 'shared/domains/forms';
import { SwitchField } from './SwitchField';
import { useDuplicateComposedData } from './useDuplicateComposedData';
import { useLatestTemplateVersionReference } from './useLatestTemplateVersionReference';
import { DuplicateDialogHeader } from '../DuplicateDialogHeader';
export function DuplicateComposedDialog(props) {
    const { documentId, documentName, onClose, onDuplicated } = props;
    const { addToast } = useToast();
    const { mutate: createDraft, state: { error: draftError }, } = useCreateDraft();
    const { draft, loading: loadingData, error: dataError } = useDuplicateComposedData(documentId);
    const { templateVersionTemplateId = '', templateVersionReference } = draft || {};
    const { latestTemplateVersionReference, loading: templateLoading, error: templateError, } = useLatestTemplateVersionReference(templateVersionTemplateId);
    const loading = loadingData || templateLoading;
    const error = Boolean(dataError || templateError);
    const hasDraftTemplateVersion = Boolean(draft === null || draft === void 0 ? void 0 : draft.templateVersionReference);
    const createPrefilledDraftDisabled = !hasDraftTemplateVersion;
    const getCreateDraftOptions = (prefillDraft) => {
        var _a;
        const { composer, result, version } = draft || {};
        if (prefillDraft) {
            return {
                result: result,
                version: version || '',
                templateVersionReference: latestTemplateVersionReference,
            };
        }
        return {
            result: [],
            version: ((_a = composer === null || composer === void 0 ? void 0 : composer.tree) === null || _a === void 0 ? void 0 : _a.version) || '',
            templateVersionReference: templateVersionReference !== null && templateVersionReference !== void 0 ? templateVersionReference : latestTemplateVersionReference,
        };
    };
    const handleSubmit = (values) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const options = Object.assign({ composerId: (draft === null || draft === void 0 ? void 0 : draft.composerId) || '', templateId: templateVersionTemplateId, templateVersionTemplateId }, getCreateDraftOptions(values.prefillDraft));
        try {
            const { data } = yield createDraft(options);
            if (onDuplicated) {
                onDuplicated(((_a = data === null || data === void 0 ? void 0 : data.created) === null || _a === void 0 ? void 0 : _a.id) || '');
            }
        }
        catch (_b) {
            addToast({
                appearance: 'danger',
                title: t({
                    message: 'Something went wrong, please try again later',
                    comment: 'Duplicate document dialog error toast',
                }),
            });
        }
    });
    if (loading || error) {
        return (<Dialog size="md" onClose={onClose}>
        <DuplicateDialogHeader testId="duplicate-composed-predata"/>
        <DialogContent>
          {loading && (<Box flexDirection="column" alignItems="center" gap="sm" p="2xl">
              <Loader size={12}/>
              <Typography>
                <Trans comment="Duplicate document dialog - Loading label">Loading...</Trans>
              </Typography>
            </Box>)}
          {error && (<Box p="2xl">
              <Alert appearance="danger" message={t({
                    message: 'Something went wrong, please try again later',
                    comment: 'Duplicate document dialog error alert message',
                })}/>
            </Box>)}
        </DialogContent>
        <DialogFooter>
          <Button size="small" appearance="secondary" onClick={onClose}>
            <Trans comment="Copy document dialog - Cancel button label">Cancel</Trans>
          </Button>
        </DialogFooter>
      </Dialog>);
    }
    return (<FormDialog size="md" initialValues={{ prefillDraft: false }} onClose={onClose} onSubmit={handleSubmit}>
      <DuplicateDialogHeader testId="duplicate-composed-data"/>
      <DialogContent>
        <Box flexDirection="column" gap="lg">
          <Typography>
            <Trans comment="Duplicate document dialog - Create a copy of the draft">
              You are creating a copy of <b>{documentName}</b>. You can create an empty copy or a copy with the same
              data as the original.
            </Trans>
          </Typography>
          {createPrefilledDraftDisabled && (<Typography $appearance="100">
              The contract template has been updated and the data can therefore not be copied.
            </Typography>)}
          <SwitchField name="prefillDraft" disabled={createPrefilledDraftDisabled} label={t({
            message: 'Copy with data',
            comment: 'Duplicate document dialog - Prefill draft switch label',
        })} description={t({
            message: 'Create a copy of your document with the same data as the original',
            comment: 'Duplicate document dialog - Prefill draft switch description',
        })}/>
          {draftError && (<Alert appearance="danger" title={t({
                message: 'Could not copy document',
                comment: 'Duplicate document dialog error alert title',
            })} message={t({
                message: 'Something went wrong, please try copying the document again',
                comment: 'Duplicate document dialog error alert message',
            })}/>)}
        </Box>
      </DialogContent>
      <DialogFooter>
        <CancelButton size="small" appearance="secondary" onClick={onClose}>
          <Trans comment="Copy document dialog - Cancel button label">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small" appearance="primary">
          <Trans comment="Copy document dialog - Copy button label">Copy document</Trans>
        </SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
